/**
 * Computer Desk - late night office desk lit by monitor glow.
 * No readable screen content is ever drawn (spec: no text in visuals).
 */

import { createRng, drawGrain, drawVignette, glow, reactive, withAlpha } from './shared.js';

const WORLD = (() => {
  const rng = createRng(0x91be4);

  const dust = Array.from({ length: 70 }, () => ({
    nx: rng(),
    ny: rng(),
    cycles: 1 + Math.floor(rng() * 2),
    offset: rng(),
    radius: 0.0008 + rng() * 0.0022,
    alpha: 0.12 + rng() * 0.4,
    drift: 0.01 + rng() * 0.03,
  }));

  const cityLights = Array.from({ length: 16 }, () => ({
    nx: 0.42 + rng() * 0.58,
    ny: rng() * 0.3,
    radius: 0.012 + rng() * 0.04,
    cycles: 1 + Math.floor(rng() * 2),
    offset: rng(),
    warm: rng(),
  }));

  const notes = Array.from({ length: 5 }, () => ({
    nx: 0.06 + rng() * 0.5,
    ny: 0.72 + rng() * 0.16,
    w: 0.08 + rng() * 0.12,
    h: 0.03 + rng() * 0.05,
    rot: (rng() - 0.5) * 0.5,
    tone: 0.55 + rng() * 0.35,
  }));

  const keys = Array.from({ length: 14 }, (_, i) => ({ i, offset: rng() }));

  return { dust, cityLights, notes, keys };
})();

export default {
  id: 'computer-desk',
  label: 'Computer Desk',
  description: 'Procedural late-night desk with monitor glow and drifting dust.',
  loopable: true,

  draw(ctx, { t, loopSeconds, w, h, audio, strength }) {
    const phase = (t % loopSeconds) / loopSeconds;
    const level = reactive(audio.level, strength);
    const bass = reactive(audio.bass, strength);
    const mid = reactive(audio.mid, strength);

    // --- room
    const room = ctx.createLinearGradient(0, 0, w * 0.4, h);
    room.addColorStop(0, '#05070A');
    room.addColorStop(0.55, '#0A0D13');
    room.addColorStop(1, '#04060A');
    ctx.fillStyle = room;
    ctx.fillRect(0, 0, w, h);

    // --- city glass wall, upper right
    ctx.save();
    ctx.beginPath();
    ctx.rect(w * 0.4, 0, w * 0.6, h * 0.34);
    ctx.clip();
    ctx.fillStyle = '#060910';
    ctx.fillRect(w * 0.4, 0, w * 0.6, h * 0.34);
    for (const c of WORLD.cityLights) {
      const breathe = 0.55 + 0.45 * Math.sin((phase + c.offset) * Math.PI * 2 * c.cycles);
      const color = c.warm > 0.6 ? '#FFB266' : c.warm > 0.3 ? '#D9E6FF' : '#7FA8FF';
      glow(ctx, c.nx * w, c.ny * h, c.radius * w, color, 0.3 * breathe * (0.7 + level * 0.5));
    }
    ctx.restore();

    // --- monitor glow flooding the desk
    const monitorX = w * 0.5;
    const monitorY = h * 0.42;
    const monitorW = w * 0.66;
    const monitorH = h * 0.2;
    const pulse = 0.85 + 0.15 * Math.sin(phase * Math.PI * 2 * 2) + level * 0.25 + bass * 0.1;

    glow(ctx, monitorX, monitorY + monitorH * 0.5, w * 1.05, '#2E6EA8', 0.16 * pulse);
    glow(ctx, monitorX, monitorY + monitorH * 0.5, w * 0.6, '#4E9AD8', 0.14 * pulse);

    // --- monitor body (no readable content, only soft shifting light)
    const screen = ctx.createLinearGradient(monitorX - monitorW / 2, monitorY, monitorX + monitorW / 2, monitorY + monitorH);
    screen.addColorStop(0, withAlpha('#1B3F5E', 0.85 * pulse));
    screen.addColorStop(0.5, withAlpha('#2C628C', 0.9 * pulse));
    screen.addColorStop(1, withAlpha('#12293D', 0.85 * pulse));
    ctx.fillStyle = screen;
    ctx.fillRect(monitorX - monitorW / 2, monitorY, monitorW, monitorH);

    // abstract on-screen glow bands (deliberately unreadable)
    for (let i = 0; i < 3; i += 1) {
      const y = monitorY + monitorH * (0.22 + i * 0.26);
      const bandPhase = (phase + i * 0.33) % 1;
      const bandW = monitorW * (0.3 + 0.25 * Math.sin(bandPhase * Math.PI * 2));
      ctx.fillStyle = withAlpha('#BFE3FF', 0.06 + mid * 0.05);
      ctx.fillRect(monitorX - monitorW / 2 + monitorW * 0.08, y, bandW, monitorH * 0.05);
    }

    // bezel
    ctx.strokeStyle = '#05070A';
    ctx.lineWidth = Math.max(4, w * 0.012);
    ctx.strokeRect(monitorX - monitorW / 2, monitorY, monitorW, monitorH);

    // stand
    ctx.fillStyle = '#070A0E';
    ctx.fillRect(monitorX - w * 0.03, monitorY + monitorH, w * 0.06, h * 0.05);
    ctx.fillRect(monitorX - w * 0.11, monitorY + monitorH + h * 0.045, w * 0.22, h * 0.014);

    // --- desk surface
    const deskTop = h * 0.68;
    const desk = ctx.createLinearGradient(0, deskTop, 0, h);
    desk.addColorStop(0, '#14171E');
    desk.addColorStop(0.25, '#0D1015');
    desk.addColorStop(1, '#05070A');
    ctx.fillStyle = desk;
    ctx.fillRect(0, deskTop, w, h - deskTop);

    // monitor light pool on the desk
    const pool = ctx.createRadialGradient(monitorX, deskTop, 0, monitorX, deskTop, w * 0.7);
    pool.addColorStop(0, withAlpha('#4E9AD8', 0.14 * pulse));
    pool.addColorStop(1, withAlpha('#4E9AD8', 0));
    ctx.fillStyle = pool;
    ctx.fillRect(0, deskTop, w, h - deskTop);

    // --- notes on the desk (abstract shapes, no writing)
    for (const n of WORLD.notes) {
      ctx.save();
      ctx.translate(n.nx * w, n.ny * h);
      ctx.rotate(n.rot);
      ctx.fillStyle = withAlpha('#E8E2D4', 0.1 * n.tone + level * 0.03);
      ctx.fillRect(0, 0, n.w * w, n.h * h);
      ctx.strokeStyle = withAlpha('#E8E2D4', 0.06);
      ctx.lineWidth = 1;
      ctx.strokeRect(0, 0, n.w * w, n.h * h);
      ctx.restore();
    }

    // --- keyboard silhouette
    const kbX = w * 0.2;
    const kbY = h * 0.86;
    const kbW = w * 0.6;
    const kbH = h * 0.075;
    ctx.fillStyle = '#080A0E';
    ctx.fillRect(kbX, kbY, kbW, kbH);
    ctx.strokeStyle = 'rgba(160,200,255,0.10)';
    ctx.lineWidth = Math.max(1, w * 0.002);
    ctx.strokeRect(kbX, kbY, kbW, kbH);
    for (const k of WORLD.keys) {
      const keyX = kbX + kbW * (0.03 + (k.i / WORLD.keys.length) * 0.93);
      const keyW = (kbW * 0.88) / WORLD.keys.length;
      const lift = 0.5 + 0.5 * Math.sin((phase + k.offset) * Math.PI * 2 * 2);
      ctx.fillStyle = withAlpha('#8FB6D8', 0.05 + lift * 0.05 + mid * 0.04);
      ctx.fillRect(keyX, kbY + kbH * 0.2, keyW * 0.8, kbH * 0.6);
    }

    // --- dust in the light beam
    for (const d of WORLD.dust) {
      const rise = (d.offset + phase * d.cycles) % 1;
      const y = (d.ny - rise * 0.35 + 1) % 1;
      const x = d.nx + Math.sin((phase + d.offset) * Math.PI * 2) * d.drift;
      const inBeam = x > 0.28 && x < 0.78 && y > 0.1 && y < 0.72;
      ctx.fillStyle = withAlpha('#CFE6FF', d.alpha * (inBeam ? 1 : 0.25));
      ctx.beginPath();
      ctx.arc(x * w, y * h, Math.max(0.6, d.radius * w), 0, Math.PI * 2);
      ctx.fill();
    }

    drawVignette(ctx, w, h, 0.6);
    drawGrain(ctx, w, h, { t, alpha: 0.03 });
  },
};
